import alphanumSort from 'helpers/sorters';
import { getDefaultAreaType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import { CropZoneRecord } from './types';

export interface SortInfo {
  columnKey: string;
  order: 'ascend' | 'descend' | null;
}

export const sortByFieldThenZone = <T extends CropZoneRecord>(
  data: T[],
  order: 'asc' | 'desc' = 'asc'
): T[] => {
  const sortedByZone = alphanumSort([...data], 'name', order);
  return alphanumSort(sortedByZone, 'fieldName', order);
};

export const getSortedData = <T extends CropZoneRecord>(
  tableData: T[],
  sortInfo: SortInfo
): T[] => {
  if (!sortInfo.order || !sortInfo.columnKey) return tableData;
  let sorted = [...tableData];
  if (sortInfo.columnKey === 'name') {
    sorted = sortByFieldThenZone(
      sorted,
      sortInfo.order === 'ascend' ? 'asc' : 'desc'
    );
  } else if (sortInfo.columnKey === 'cropName') {
    sorted = alphanumSort(
      sorted,
      'crop.name',
      sortInfo.order === 'ascend' ? 'asc' : 'desc'
    );
  } else if (sortInfo.columnKey === 'propertyName') {
    sorted = alphanumSort(
      sorted,
      'propertyName',
      sortInfo.order === 'ascend' ? 'asc' : 'desc'
    );
  }
  return sorted;
};

export const ensureMinimumValue = (value: number): number =>
  value <= 0 ? 1 : value;

/**
 * Fills in an unresolved crop zone's editable/area-type defaults the first
 * time it's seen — matches legacy `PlanPropertiesTab`/`recalculate`'s own
 * mapping so a freshly-selected or freshly-loaded zone lands with sane
 * `appliedAcreage`/`coveragePercent`/`boundaryArea`/`selectedAreaType`.
 */
export const getMappedData = <T extends CropZoneRecord>(data: T[]): T[] =>
  data.map(zone => ({
    ...zone,
    appliedAcreage: zone.appliedAcreage ?? zone.area,
    coveragePercent: zone.coveragePercent ?? 100,
    reportedArea: zone.reportedArea,
    boundaryArea: zone.boundaryArea || zone.area,
    selectedAreaType:
      zone.selectedAreaType || getDefaultAreaType(zone.reportedArea)
  }));
